import { Paperclip, Download, AlertCircle, Clock } from "lucide-react";
import React, { useState } from "react";
import {
  Modal,
  Button,
  Text,
  Group,
  Stack,
  Badge,
  LoadingOverlay,
  Alert,
  Space,
} from "@mantine/core";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ReconFilesListResponse,
  ReconFileInfo,
} from "../services/model/ReconFile";
import useGetReconFileApiService from "../services/api/GetReconFileApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";

// Configure dayjs plugin
dayjs.extend(relativeTime);

interface MultipleReconFilesModalProps {
  opened: boolean;
  onClose: () => void;
  filesData: ReconFilesListResponse | undefined;
  employerId: string;
  isLoading: boolean;
  error: any;
}

const MultipleReconFilesModal: React.FC<MultipleReconFilesModalProps> = ({
  opened,
  onClose,
  filesData,
  employerId,
  isLoading,
  error,
}) => {
  const { showToast } = useToasts();
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set()
  );

  const { mutate: downloadReconFile } = useGetReconFileApiService();

  const handleDownloadFile = (file: ReconFileInfo): void => {
    setDownloadingFiles((prev) => {
      const next = new Set(prev);
      next.add(file.filename);
      return next;
    });

    downloadReconFile(
      {
        employerId,
        reconFile: file.filename,
      },
      {
        onSuccess: (response) => {
          if (response?.url) {
            window.open(response.url, "_blank")?.focus();
            showToast({
              strapiId: "recon_file_download_success",
            });
          }
        },
        onError: (downloadError) => {
          showToast({
            strapiId: "recon_file_download_failed",
            data: {
              message:
                downloadError?.response?.data?.message || "Download failed",
            },
          });
        },
        onSettled: () => {
          setDownloadingFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(file.filename);
            return newSet;
          });
        },
      }
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = Number((bytes / k ** i).toFixed(2));
    return `${value} ${sizes[i]}`;
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "Unknown";
    try {
      return dayjs(dateString).fromNow();
    } catch (err) {
      console.warn("Failed to parse date:", dateString, err);
      return "Unknown";
    }
  };

  const renderFilesList = (): React.ReactNode => {
    if (isLoading) {
      return <LoadingOverlay visible />;
    }

    if (error) {
      return (
        <Alert icon={<AlertCircle size={16} />} color="red" radius="md">
          <Text size="sm" weight={500}>
            Failed to load recon files
          </Text>
          <Text size="xs" color="dimmed" style={{ marginTop: "4px" }}>
            {error?.response?.data?.error || "Please try again later"}
          </Text>
        </Alert>
      );
    }

    if (!filesData?.files || filesData.files.length === 0) {
      return (
        <Stack align="center" spacing="md" style={{ padding: "32px 16px" }}>
          <Text size="md" weight={500} color="dimmed" align="center">
            No recon files found
          </Text>
          <Text size="sm" color="dimmed" align="center">
            No files are available for this recon date
          </Text>
        </Stack>
      );
    }

    return (
      <Stack spacing="md">
        {filesData.files.map((file) => {
          const isDownloading = downloadingFiles.has(file.filename);

          return (
            <div
              key={file.filename}
              style={{
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                padding: "16px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <Group position="apart" align="flex-start">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Group spacing="xs" mb="xs">
                    <Paperclip size={16} />
                    <Text
                      size="sm"
                      weight={500}
                      style={{
                        wordBreak: "break-all",
                        lineHeight: 1.4,
                      }}
                    >
                      {file.filename}
                    </Text>
                  </Group>

                  <Group spacing="md">
                    <Badge size="sm" color="gray" variant="light">
                      {formatFileSize(file.size)}
                    </Badge>

                    {file.created && (
                      <Group spacing={4}>
                        <Clock size={12} />
                        <Text size="xs" color="dimmed">
                          {formatDate(file.created)}
                        </Text>
                      </Group>
                    )}
                  </Group>
                </div>

                <Button
                  leftIcon={<Download size={14} />}
                  size="sm"
                  variant="outline"
                  color="jem-theme"
                  loading={isDownloading}
                  onClick={() => handleDownloadFile(file)}
                  style={{ flexShrink: 0 }}
                >
                  Download
                </Button>
              </Group>
            </div>
          );
        })}
      </Stack>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <Text weight={600}>Recon Files</Text>
          {filesData?.count && (
            <Badge color="jem-theme" variant="light">
              {filesData.count} {filesData.count === 1 ? "file" : "files"}
            </Badge>
          )}
        </Group>
      }
      size="lg"
      centered
      overlayOpacity={0.1}
      overlayBlur={3}
    >
      <div style={{ minHeight: "200px" }}>
        {filesData?.date && (
          <>
            <Text size="sm" color="dimmed" mb="md">
              Showing files for recon date: <strong>{filesData.date}</strong>
            </Text>
            <Space h="sm" />
          </>
        )}

        {renderFilesList()}

        <Space h="lg" />

        <Group position="right">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </div>
    </Modal>
  );
};

export default MultipleReconFilesModal;
